import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
} from '@mui/material';

interface RenameSubcaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (oldSubcase: string, newSubcase: string) => void;
  subcases: string[];
}

const RenameSubcaseDialog: React.FC<RenameSubcaseDialogProps> = ({
  open,
  onClose,
  onSubmit,
  subcases,
}) => {
  const [selectedSubcase, setSelectedSubcase] = useState<string>('');
  const [newSubcase, setNewSubcase] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedSubcase('');
      setNewSubcase('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    // Validate uniqueness of new subcase name
    const trimmedNewSubcase = newSubcase.trim();
    if (!trimmedNewSubcase) {
      setError(null);
      return;
    }
    if (
      subcases
        .filter((s) => s !== selectedSubcase) // exclude the one being renamed
        .map((s) => s.trim())
        .includes(trimmedNewSubcase)
    ) {
      setError('A subcase with this name already exists.');
    } else {
      setError(null);
    }
  }, [newSubcase, selectedSubcase, subcases]);

  const handleSubmit = () => {
    if (selectedSubcase && newSubcase && !error) {
      const trimmedOldSubcase = selectedSubcase.trim();
      const trimmedNewSubcase = newSubcase.trim();
      
      // Validate that trimmed values are not empty
      if (!trimmedOldSubcase || !trimmedNewSubcase) {
        return;
      }
      
      // Validate that new subcase name is different
      if (trimmedOldSubcase === trimmedNewSubcase) {
        return;
      }
      
      // Validate uniqueness
      if (
        subcases
          .filter((s) => s !== selectedSubcase)
          .map((s) => s.trim())
          .includes(trimmedNewSubcase)
      ) {
        setError('A subcase with this name already exists.');
        return;
      }
      
      onSubmit(trimmedOldSubcase, trimmedNewSubcase);
      setSelectedSubcase('');
      setNewSubcase('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Rename Subcase</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Autocomplete
            options={subcases}
            value={selectedSubcase}
            onChange={(_, newValue) => setSelectedSubcase(newValue || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Subcase to Rename"
                required
              />
            )}
          />
          <TextField
            label="New Subcase Name"
            required
            fullWidth
            value={newSubcase}
            onChange={(e) => setNewSubcase(e.target.value)}
            error={!!error}
            helperText={error}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !selectedSubcase.trim() ||
            !newSubcase.trim() ||
            selectedSubcase.trim() === newSubcase.trim() ||
            !!error
          }
        >
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameSubcaseDialog; 